import React, { useCallback, useState } from "react";
import {
  Col,
  Table,
  Input,
  Button,
  DatePicker,
  Skeleton,
  AutoComplete,
  Row,
} from "antd";
import debounce from "lodash/debounce";
import {
  ExportOutlined,
  ImportOutlined,
  PlusSquareOutlined,
  HomeOutlined
} from "@ant-design/icons";
import moment from "moment";

import "./index.css";
import { DateFormat } from "../../helpers";

export default function TableContainer({
  columns,
  data,
  loading,
  total = 8,
  functionsHandler,
  rowClickHandler,
  searchFor = "Name, Sub-Category",
  dateRangePicker = false,
  addButton = false,
  addHandler,
  parseButton = "",
  parseHandler,
  parsing,
  dropdownFilterFor = "Category",
  tagFilterOptions = [],
  dropdownFilterOptions = [],
  dropdownSearchHandler,
  scrollable = true,
  breadcrumbTitle,
  children,
  customFilterComponent = null,
  combineFilters = false,
  ...otherProps
}) {
  const [page, setPage] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [dates, setDates] = useState([]);
  const [tag, setTag] = useState("");
  const [dropdown, setDropdown] = useState("");
  const [payload, setPayload] = useState({ size: 10, keyword: "" });
  const [isSearching, setIsSearching] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const paginationHandler = (pageNo, pageSize) => {
    setPage(pageNo - 1);
    let tempPayload = { ...payload, page: pageNo - 1, size: pageSize };
    functionsHandler(tempPayload);
    setPayload(tempPayload);
  };

  const sorterHandler = (sort, extra) => {
    let sortObj = {};
    if (sort.field && extra.action === "sort") {
      setPage(0);
      if (sort.order === "ascend") {
        sortObj = { field: sort.field, order_by: "asc" };
      } else if (sort.order === "descend") {
        sortObj = { field: sort.field, order_by: "desc" };
      }
      let tempPayload = { ...payload, sort: sortObj, page: 0 };
      functionsHandler(tempPayload);
      setPayload(tempPayload);
    }
  };

  const searchFilterHandler = (keyword) => {
    if (keyword !== payload.keyword) {
      setPage(0);
      setKeyword(keyword);
      if (!combineFilters) {
        setTag("");
        setDates([]);
        setDropdown("");
      }
      setIsSearching(true);
      setIsInitialLoad(false);
      let tempPayload = combineFilters
        ? {
            ...payload,
            keyword: keyword,
            page: 0,
          }
        : {
            keyword: keyword,
            sort: payload.sort,
            size: payload.size,
          };
      functionsHandler(tempPayload);
      setPayload(tempPayload);
    }
  };

  // Debounced search handler for text input
  const debouncedTextSearch = useCallback(
    debounce((searchValue) => {
      searchFilterHandler(searchValue);
    }, 500),
    [payload]
  );

  // Reset searching state when loading completes
  React.useEffect(() => {
    if (!loading && isSearching) {
      setIsSearching(false);
    }
    if (!loading && isInitialLoad && data.length > 0) {
      setIsInitialLoad(false);
    }
  }, [loading, isSearching, isInitialLoad, data]);

  const dateRangeFilterHandler = (dates, dateStrings, info) => {
    setPage(0);
    if (!combineFilters) {
      setKeyword("");
      setTag("");
      setDropdown("");
    }
    setIsSearching(true);
    setIsInitialLoad(false);
    let tempPayload = combineFilters
      ? {
          ...payload,
          page: 0,
        }
      : {};
    if (dates) {
      tempPayload = {
        ...tempPayload,
        start_date: DateFormat(dates[0], "YYYY-MM-DD"),
        end_date: DateFormat(dates[1], "YYYY-MM-DD"),
        size: payload.size,
        sort: payload.sort,
      };
      setDates(dates);
    } else {
      if (combineFilters) {
        delete tempPayload.start_date;
        delete tempPayload.end_date;
      }
      setDates([]);
    }
    functionsHandler(tempPayload);
    setPayload(tempPayload);
  };

  const tagFilterHandler = (selectedValue = "") => {
    let tempPayload = combineFilters
      ? {
          ...payload,
          page: 0,
        }
      : {
          sort: payload.sort,
          size: payload.size,
        };
    setPage(0);
    if (!combineFilters) {
      setDates([]);
      setKeyword("");
      setDropdown("");
    }
    setIsSearching(true);
    setIsInitialLoad(false);
    delete tempPayload.from;
    delete tempPayload.to;
    delete tempPayload.filter;
    if (selectedValue && selectedValue !== tag) {
      setTag(selectedValue);
      tempPayload = { filter: selectedValue, ...tempPayload };
      functionsHandler(tempPayload);
      setPayload(tempPayload);
    } else {
      setTag("");
      functionsHandler(tempPayload);
      setPayload(tempPayload);
    }
  };

  const dropdownFilterHandler = (selectedDropdown) => {
    const selectedId =
      selectedDropdown?.key ??
      selectedDropdown?.id ??
      dropdownFilterOptions.find(
        (option) =>
          option.value === selectedDropdown?.value ||
          option.value === selectedDropdown?.label ||
          option.value === selectedDropdown
      )?.key;

    let tempPayload = combineFilters
      ? {
          ...payload,
          page: 0,
          size: payload.size,
          sort: payload.sort,
        }
      : {
          size: payload.size,
          sort: payload.sort,
        };
    if (selectedDropdown) {
      setPage(0);
      if (!combineFilters) {
        setKeyword("");
        setDates([]);
        setTag("");
      }
      setIsSearching(true);
      setIsInitialLoad(false);
      if (selectedId === undefined || selectedId === null) {
        return;
      }
      if (dropdownFilterFor === "Product") {
        tempPayload = {
          ...tempPayload,
          product_id: selectedId,
        };
      } else {
        tempPayload = {
          ...tempPayload,
          category_id: selectedId,
        };
      }
    } else {
      setDropdown("");
      delete tempPayload.category_id;
      delete tempPayload.product_id;
    }
    functionsHandler(tempPayload);
    setPayload(tempPayload);
  };

  const debouncedSearch = useCallback(
    debounce((nextValue) => dropdownSearchHandler(nextValue), 1200),
    []
  );

  const handleTableChange = (_, __, sorter, extra) => {
    // Pagination is already handled by pagination.onChange.
    // Handle sorting only to avoid duplicate API calls with partial payloads.
    if (extra?.action === "sort") {
      sorterHandler(sorter, extra);
    }
  };

  return (
    <>
      <div className="breadcrumb-search-row">
        {breadcrumbTitle && (
          <div className="breadcrumb-container">
            <div className="breadcrumb-content">
              <HomeOutlined /> <span className="breadcrumb-title"> / {breadcrumbTitle}</span>
            </div>
          </div>
        )}
        <div className="search-controls">
          {dropdownFilterOptions.length > 0 && (
            <AutoComplete
            allowClear
            showArrow
            style={{ width: 225 }}
            options={dropdownFilterOptions}
            onSelect={(value, option) => dropdownFilterHandler(option || value)}
            onSearch={(value) => value.length > 1 && debouncedSearch(value)}
            placeholder={"Select " + dropdownFilterFor}
            value={dropdown}
            onChange={(v) => {
              if (!v && (payload.category_id || payload.product_id)) {
                dropdownFilterHandler(null);
              } else {
                setDropdown(v);
              }
            }}
          />
        )}
        {dateRangePicker && (
          <DatePicker.RangePicker
            allowClear
            style={{ width: 255, marginLeft: 20 }}
            format="DD-MM-YYYY"
            value={dates}
            onChange={dateRangeFilterHandler}
            disabledDate={(current) =>
              current && current > moment().endOf("day")
            }
          />
        )}
        <Input.Search
          style={{ width: 255, marginLeft: 20 }}
          placeholder={"Search " + searchFor}
          allowClear
          enterButton
          loading={isSearching && loading}
          value={keyword}
          onChange={(e) => {
            const value = e.target.value;
            setKeyword(value);
            debouncedTextSearch(value);
          }}
          onSearch={searchFilterHandler}
        />
        {addButton && (
          <Button
            style={{ marginLeft: 20 }}
            onClick={addHandler}
            type="primary"
            loading={loading}
            icon={<PlusSquareOutlined />}
          >
            Add
          </Button>
        )}
        {parseButton && (
          <Button
            loading={parsing || loading}
            style={{
              marginLeft: 20,
              textTransform: "capitalize",
            }}
            onClick={parseHandler}
            type="primary"
            icon={
              parseButton === "export" ? (
                <ExportOutlined />
              ) : parseButton === "import" ? (
                <ImportOutlined />
              ) : <PlusSquareOutlined />
            }
          >
            {parseButton}
          </Button>
        )}
        {customFilterComponent && (
          <div style={{ marginLeft: 20 }}>
            {customFilterComponent}
          </div>
        )}
        </div>
      </div>
      {tagFilterOptions.length > 0 && (
        <Col span={24}>
          <Button
            style={{ marginTop: 15, marginRight: 10 }}
            onClick={() => tagFilterHandler("")}
            type={!tag && "primary"}
          >
            All
          </Button>
          {tagFilterOptions.map((tg) => (
            <Button
              type={tg.value === tag && "primary"}
              onClick={() => tagFilterHandler(tg.value)}
              style={{ marginRight: 10, marginTop: 15 }}
              key={tg.value}
            >
              {tg.label}
            </Button>
          ))}
        </Col>
      )}
      <Table
        scroll={{ x: scrollable }}
        size="small"
        className={`tbl ${isSearching && loading ? 'table-searching' : ''}`}
        rowClassName="rww"
        bordered
        style={{
          opacity: isSearching && loading ? 0.6 : 1,
          transition: 'opacity 0.3s ease-in-out'
        }}
        columns={
          loading && isInitialLoad
            ? [
              {
                title: "Sr. No.",
                key: "sno",
                width: 60,
                render: () => (
                  <Skeleton key="sno" round active paragraph={false} />
                ),
              },
              ...columns.map((column) => {
                return {
                  ...column,
                  render: function renderPlaceholder() {
                    return (
                      <Skeleton
                        key={column.key}
                        round
                        active
                        paragraph={false}
                      />
                    );
                  },
                };
              }),
            ]
            : [
              {
                title: "Sr. No.",
                key: "sno",
                width: 60,
                align: "center",
                render: (text, record, index) =>
                  page * payload.size + index + 1,
              },
              ...columns,
            ]
        }
        dataSource={
          loading && isInitialLoad
            ? [...Array(6)].map((_, index) => ({
              key: `key${index}`,
            }))
            : data
        }
        pagination={{
          style: { marginBottom: -5 },
          total: total,
          current: page + 1,
          hideOnSinglePage: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          onChange: (page, pageSize) => paginationHandler(page, pageSize),
        }}
        onChange={handleTableChange}
        onRow={(record, rowIndex) => {
          return {
            onClick: (event) => {
              if (!loading) {
                rowClickHandler(record);
              }
            },
          };
        }}
        {...otherProps}
      ></Table>
    </>
  );
}
